export enum SlideId {
  organisation = 'organisation',
  customers = 'customers',
  softwareDev = 'softwareDev',
  supplyChain = 'supplyChain',
  cyberInsurance = 'cyberInsurance',
  regulations = 'regulations',
  assessmentDetails = 'assessmentDetails',
  scenarios = 'scenarios',
  finalise = 'finalise',
  customControls = 'recommenderControls',
}

export enum RecomendedControlsSlideId {
  scenarios = 'scenarios',
  controls = 'controls',
  link = 'link',
}

export interface FormValuesScenarios {
  B2B: boolean;
  B2C: boolean;
  publiclyAccessibleInternetBusiness: boolean;
  paymentVolumeOrValue: boolean;
  significantITContributionToRevenue: boolean;
  significantDataInCloud: boolean;
  softwareBusiness: boolean;
  cniOrDefenseInvolvement: boolean;
  significantRandD: boolean;
  regulated: boolean;
  operationalTechnology: boolean;
  supplyChainReliant: boolean;
  aiThirdPartyReliant: boolean;
  aiCustomerFacingApp: boolean;
}

export interface WizardFormValues {
  organisation: {
    sector?: CompanySector;
    usesManagedServiceProvider: boolean;
    annualRevenue?: number | null; //variable
    numberOfStaff?: number | null; //variable
    annualWageBill?: number | null; //variable
    avgWorkingDays?: number | null; //variable
    annualCyberSpend?: number | null; //variable
    avgStaffHardwareCost?: number | null; //variable
  };
  customers: {
    numberOfCustomers?: number | null; //variable
    avgContractValue?: number | null; //variable
    numberOfDataRecords?: number | null; //variable
  };
  softwareDev: {
    developsSoftware: boolean;
    releaseCycleCost?: number | null; //variable
    releaseCycleLength?: number | null; //variable
    researchAndDevelopmentBudget?: number | null; //variable
  };
  supplyChain: {
    avgCostToProcureSupplier?: number | null; //variable
    avgInvoiceValue?: number | null; //variable
    largestRegularInvoiceValue?: number | null; //variable
  };
  cyberInsurance: {
    cyberInsurance: boolean;
    cyberInsuranceLimit?: number | null; //variable
    cyberInsuranceExcess?: number | null; //variable
    cyberInsurancePremium?: number | null; //variable
  };
  regulations: {
    legislation: string[];
    controlFrameworks: string[];
    controlFrameworksOther: string[];
    primaryControlFramework?: string;
    primaryFrameworkControls: string[];
  };
  scenarios: FormValuesScenarios;
  assessment: {
    id?: string;
    name?: string | null;
    description?: string | null;
    started_date?: string | null;
    review_date?: string | null;
  };
}

export interface WizardRecomendedControlsFormValues {
  scenarios: FormValuesScenarios;
  selectedControls: Record<string, Record<string, boolean>>; // controlId -> scenarioId -> isSelected
  addRecommendedModifiers: boolean;
}

// ----------------------------------------------------   Company sector
export enum CompanySector {
  TECHNOLOGY = 'TECHNOLOGY',
  TELECOMMUNICATIONS = 'TELECOMMUNICATIONS',
  HEALTH_CARE = 'HEALTH_CARE',
  BANKS = 'BANKS',
  FINANCIAL_SERVICES = 'FINANCIAL_SERVICES',
  INSURANCE = 'INSURANCE',
  REAL_ESTATE = 'REAL_ESTATE',
  AUTOMOBILES_AND_PARTS = 'AUTOMOBILES_AND_PARTS',
  CONSUMER_PRODUCTS_AND_SERVICES = 'CONSUMER_PRODUCTS_AND_SERVICES',
  MEDIA = 'MEDIA',
  RETAIL = 'RETAIL',
  TRAVEL_AND_LEISURE = 'TRAVEL_AND_LEISURE',
  FOOD_BEVERAGE_AND_TOBACCO = 'FOOD_BEVERAGE_AND_TOBACCO',
  PERSONAL_CARE_DRUG_AND_GROCERY_STORES = 'PERSONAL_CARE_DRUG_AND_GROCERY_STORES',
  CONSTRUCTION_AND_MATERIALS = 'CONSTRUCTION_AND_MATERIALS',
  INDUSTRIAL_GOODS_AND_SERVICES = 'INDUSTRIAL_GOODS_AND_SERVICES',
  BASIC_RESOURCES = 'BASIC_RESOURCES',
  CHEMICALS = 'CHEMICALS',
  ENERGY = 'ENERGY',
  UTILITIES = 'UTILITIES',
  AEROSPACE_AND_DEFENCE = 'AEROSPACE_AND_DEFENCE',
}

export const companySectorNaming = {
  [CompanySector.TECHNOLOGY]: 'Technology',
  [CompanySector.TELECOMMUNICATIONS]: 'Telecommunications',
  [CompanySector.HEALTH_CARE]: 'Health Care',
  [CompanySector.BANKS]: 'Banks',
  [CompanySector.FINANCIAL_SERVICES]: 'Financial Services',
  [CompanySector.INSURANCE]: 'Insurance',
  [CompanySector.REAL_ESTATE]: 'Real Estate',
  [CompanySector.AUTOMOBILES_AND_PARTS]: 'Automobiles and Parts',
  [CompanySector.CONSUMER_PRODUCTS_AND_SERVICES]: 'Consumer Products and Services',
  [CompanySector.MEDIA]: 'Media',
  [CompanySector.RETAIL]: 'Retail',
  [CompanySector.TRAVEL_AND_LEISURE]: 'Travel and Leisure',
  [CompanySector.FOOD_BEVERAGE_AND_TOBACCO]: 'Food, Beverage and Tobacco',
  [CompanySector.PERSONAL_CARE_DRUG_AND_GROCERY_STORES]: 'Personal Care, Drug and Grocery Stores',
  [CompanySector.CONSTRUCTION_AND_MATERIALS]: 'Construction and Materials',
  [CompanySector.INDUSTRIAL_GOODS_AND_SERVICES]: 'Industrial Goods and Services',
  [CompanySector.BASIC_RESOURCES]: 'Basic Resources',
  [CompanySector.CHEMICALS]: 'Chemicals',
  [CompanySector.ENERGY]: 'Energy',
  [CompanySector.UTILITIES]: 'Utilities',
  [CompanySector.AEROSPACE_AND_DEFENCE]: 'Aerospace and Defence',
};

export const companySectorOptions: { label: string; value: CompanySector }[] = [
  {
    value: CompanySector.TECHNOLOGY,
    label: companySectorNaming[CompanySector.TECHNOLOGY],
  },
  {
    value: CompanySector.TELECOMMUNICATIONS,
    label: companySectorNaming[CompanySector.TELECOMMUNICATIONS],
  },
  {
    value: CompanySector.HEALTH_CARE,
    label: companySectorNaming[CompanySector.HEALTH_CARE],
  },
  {
    value: CompanySector.BANKS,
    label: companySectorNaming[CompanySector.BANKS],
  },
  {
    value: CompanySector.FINANCIAL_SERVICES,
    label: companySectorNaming[CompanySector.FINANCIAL_SERVICES],
  },
  {
    value: CompanySector.INSURANCE,
    label: companySectorNaming[CompanySector.INSURANCE],
  },
  {
    value: CompanySector.REAL_ESTATE,
    label: companySectorNaming[CompanySector.REAL_ESTATE],
  },
  {
    value: CompanySector.AUTOMOBILES_AND_PARTS,
    label: companySectorNaming[CompanySector.AUTOMOBILES_AND_PARTS],
  },
  {
    value: CompanySector.CONSUMER_PRODUCTS_AND_SERVICES,
    label: companySectorNaming[CompanySector.CONSUMER_PRODUCTS_AND_SERVICES],
  },
  {
    value: CompanySector.MEDIA,
    label: companySectorNaming[CompanySector.MEDIA],
  },
  {
    value: CompanySector.RETAIL,
    label: companySectorNaming[CompanySector.RETAIL],
  },
  {
    value: CompanySector.TRAVEL_AND_LEISURE,
    label: companySectorNaming[CompanySector.TRAVEL_AND_LEISURE],
  },
  {
    value: CompanySector.FOOD_BEVERAGE_AND_TOBACCO,
    label: companySectorNaming[CompanySector.FOOD_BEVERAGE_AND_TOBACCO],
  },
  {
    value: CompanySector.PERSONAL_CARE_DRUG_AND_GROCERY_STORES,
    label: companySectorNaming[CompanySector.PERSONAL_CARE_DRUG_AND_GROCERY_STORES],
  },
  {
    value: CompanySector.CONSTRUCTION_AND_MATERIALS,
    label: companySectorNaming[CompanySector.CONSTRUCTION_AND_MATERIALS],
  },
  {
    value: CompanySector.INDUSTRIAL_GOODS_AND_SERVICES,
    label: companySectorNaming[CompanySector.INDUSTRIAL_GOODS_AND_SERVICES],
  },
  {
    value: CompanySector.BASIC_RESOURCES,
    label: companySectorNaming[CompanySector.BASIC_RESOURCES],
  },
  {
    value: CompanySector.CHEMICALS,
    label: companySectorNaming[CompanySector.CHEMICALS],
  },
  {
    value: CompanySector.ENERGY,
    label: companySectorNaming[CompanySector.ENERGY],
  },
  {
    value: CompanySector.UTILITIES,
    label: companySectorNaming[CompanySector.UTILITIES],
  },
  {
    value: CompanySector.AEROSPACE_AND_DEFENCE,
    label: companySectorNaming[CompanySector.AEROSPACE_AND_DEFENCE],
  },
];
